import { useContext, useEffect, useMemo, useState } from 'react';

import { personAvailabilityChannelName } from '@/channels/utils/channelNames';
import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import { useChannelData } from '@/hooks/useChannel';
import PersonContext from '@/pages/Person/PersonContext';
import PersonAvailabilityRepository from '@/repositories/PersonAvailabilityRepository';
import { PersonAvailability } from '@/types/personAvailability';
import { Container, Spinner } from '@/ui';
import Accordion from '@/ui/components/Accordion';

import MemberAvailabilitySection from '../Profile/components/MemberAvailabilitySection';

import DateSpecificHoursSection from './components/DateSpecificHoursSection';
import WeeklyHoursSection from './components/WeeklyHoursSection';

const formatDateForAPI = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

const PersonAvailabilityPage = () => {
  const { person } = useContext(PersonContext);
  const { addMessage } = useContext(AppContext);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const availabilityRepo = PersonAvailabilityRepository();

  // Load availability for the next 60 days
  const availabilityDateRange = useMemo(() => {
    const today = new Date(new Date().toDateString());

    const sixtyDaysFromNow = new Date(today);
    sixtyDaysFromNow.setDate(today.getDate() + 60);

    return {
      from_date: formatDateForAPI(today),
      until_date: formatDateForAPI(sixtyDaysFromNow)
    };
  }, []);

  const {
    data: availabilityData,
    isPending,
    refetch
  } = useAPI({
    queryKey: [
      'availability',
      person.id,
      availabilityDateRange.from_date,
      availabilityDateRange.until_date
    ],
    queryFn: async () => {
      const response = await availabilityRepo.listByPersonId({
        personId: person.id,
        params: availabilityDateRange
      });
      return response.data;
    }
  });

  // Subscribe to availability updates via WebSocket
  useChannelData({
    channelId: personAvailabilityChannelName(person.id),
    channelName: 'Person Availability',
    messageCallback: (message) => {
      if (message.name === 'availability_updated') {
        addMessage('success', 'Availability updated');
        refetch();
      }
    }
  });

  const availability = useMemo((): PersonAvailability | null => {
    if (Array.isArray(availabilityData?.results) && availabilityData.results.length > 0) {
      return availabilityData.results[0];
    }
    return null;
  }, [availabilityData]);

  useEffect(() => {
    if (availability?.timezone) {
      setSelectedTimezone(availability.timezone);
    } else {
      setSelectedTimezone(person.time_zone || 'America/New_York');
    }
  }, [availability, person.time_zone]);

  if (isPending) return <Spinner />;

  return (
    <Container>
      <div className="mx-auto py-4 px-3 sm:py-6 sm:px-4">
        <PageHeader headingText="Member Availability" />
        <Accordion defaultOpen={true} buttonText="✨ Member Availability Notes">
          <MemberAvailabilitySection showSummaryEditAction={false} />
        </Accordion>

        <div className="space-y-6 mt-6">
          <WeeklyHoursSection
            rules={availability?.rules || []}
            availabilityId={availability?.id}
            personId={person.id}
            timezone={selectedTimezone}
            onUpdate={refetch}
          />
          <DateSpecificHoursSection
            rules={availability?.rules || []}
            availabilityId={availability?.id}
            personId={person.id}
            timezone={selectedTimezone}
            onUpdate={refetch}
          />
        </div>

        {availability && (
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded">
            <p className="text-xs sm:text-sm text-gray-600 break-words">
              <strong>Person&apos;s Timezone:</strong> {selectedTimezone}
            </p>
          </div>
        )}
      </div>
    </Container>
  );
};

export default PersonAvailabilityPage;
